<template>
    <div class="w-full flex-auto flex flex-col p-8 bg-gradient-to-br from-gray-100 to-blue-50">
        <div class="h-full w-full container mx-auto">
            <div class="h-full flex flex-col bg-white rounded-2xl shadow-lg p-8">
                <!-- Header -->
                <div class="mb-6">
                    <h1 class="text-2xl font-bold text-gray-800">💡 Think Tank Details</h1>
                    <p class="text-sm text-gray-500">Submitted on {{ formatDate(item.created_at) }}</p>

                    <!-- Submitter Info -->
                    <div v-if="item.user" class="mt-3 bg-gray-50 border rounded-lg p-4 text-sm">
                        <p class="text-gray-600">
                            <span class="font-semibold text-gray-700 mr-1">Submitted by:</span>{{ item.user.name }}
                        </p>
                        <p class="text-gray-600">
                            <span class="font-semibold text-gray-700">Email:</span>
                            <a :href="`mailto:${item.user.email}`" class="ml-1 text-blue-600 hover:underline">{{
                                item.user.email }}</a>
                        </p>
                        <p v-if="item.user.phone_number" class="text-gray-600">
                            <span class="font-semibold text-gray-700">Phone:</span>
                            <a :href="`tel:${item.user.phone_number}`" class="ml-1 hover:underline">{{
                                item.user.phone_number
                            }}</a>
                        </p>
                        <p v-if="item.user.entity" class="text-gray-600">
                            <span class="font-semibold text-gray-700">{{ primaryLabel(item.user.entity) }}:</span>
                            <span class="ml-1">{{ primaryEntity(item.user.entity) }}</span>
                        </p>
                        <p v-if="item.user.entity && secondaryEntities(item.user.entity).length" class="text-gray-600">
                            <span class="font-semibold text-gray-700">{{ secondaryLabel(item.user.entity) }}:</span>
                            <span class="ml-1">{{ secondaryEntities(item.user.entity).join(', ') }}</span>
                        </p>
                        <p v-if="item.user.campuses" class="text-gray-600">
                            <span class="font-semibold text-gray-700">Campus:</span>
                            <span class="ml-1">{{ item.user.campuses.split(',')[0] }}</span>
                        </p>
                    </div>
                </div>

                <!-- Short Description -->
                <div class="mb-6">
                    <h2 class="text-lg font-semibold text-gray-700 mb-1">📝 Summary</h2>
                    <p class="text-gray-800 leading-relaxed break-all">{{ item.short_description }}</p>
                </div>

                <!-- Description -->
                <div class="mb-6">
                    <h2 class="text-lg font-semibold text-gray-700 mb-1">🧠 Detailed Description</h2>
                    <p class="text-gray-800 leading-relaxed break-word">{{ item.description }}</p>
                </div>

                <!-- Budget Info -->
                <div class="mb-6">
                    <h2 class="text-lg font-semibold text-gray-700 mb-1">💰 Budget</h2>
                    <p>
                        <span class="font-medium text-gray-600 mr-2">Required:</span>
                        <span :class="item.budget_required ? 'text-green-600 font-semibold' : 'text-gray-500'">
                            {{ item.budget_required ? 'Yes' : 'No' }}
                        </span>
                    </p>
                    <p v-if="item.budget_required" class="mt-1 flex items-center">
                        <span class="font-medium text-gray-600 mr-2">Estimated Amount:</span>
                        <img class="h-3" src="../../assets/images/default/DirhamBlack.svg" alt="AED">
                        <span class="ml-1 font-semibold">{{ item.estimated_amount }}</span>
                    </p>
                </div>

                <!-- Attachments -->
                <div v-if="item.attachments.length" class="mb-6">
                    <h2 class="text-lg font-semibold text-gray-700 mb-2">📎 Uploaded Files</h2>
                    <ul class="list-disc list-inside space-y-1">
                        <li v-for="(file, index) in item.attachments" :key="index">
                            <a :href="getUrl(file.file_path)" target="_blank" class="text-blue-600 hover:underline">
                                {{ file.file_name }}
                            </a>
                        </li>
                    </ul>
                </div>

                <!-- Back Button -->
                <div class="mt-8 flex-auto flex flex-col justify-end items-end">
                    <div @click.stop="$emit('close')"
                        class="inline-flex items-center px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition cursor-pointer">
                        ← Back to List
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: ["item"],
    methods: {
        primaryEntity(entityStr) {
            const list = (entityStr || '').split(',').map(s => s.trim()).filter(Boolean);
            return list.length ? this.mapAths(list[0]) : '';
        },
        primaryLabel(entityStr) {
            const list = (entityStr || '').split(',').map(s => s.trim()).filter(Boolean);
            return list.length <= 1 ? 'Entity' : 'Primary Entity';
        },
        secondaryEntities(entityStr) {
            const list = (entityStr || '').split(',').map(s => s.trim()).filter(Boolean);
            if (list.length <= 1) return [];
            return list.slice(1).map(this.mapAths);
        },
        secondaryLabel(entityStr) {
            const count = this.secondaryEntities(entityStr).length;
            return count <= 1 ? 'Secondary Entity' : 'Secondary Entities';
        },
        mapAths(value) {
            return value === 'ATHS' ? 'ATS' : value;
        },
        formatDate(dateStr) {
            const d = new Date(dateStr)
            return d.toLocaleDateString('en-GB', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            })
        },
        getUrl(url) {
            return process.env.VUE_APP_PUBLIC_URL + url;
        },
    }
}
</script>
